import { useEffect, useMemo, useRef, useState } from "react";
import { useServerApi } from "./userServerApi";
import { getClientRpqSummary } from "../helpers/arFormUtils";
import { resolveRpqStatus } from "../helpers/clientRpqStatus";

/**
 * Resolves client RPQ for order forms.
 * RPQ is stored on the client record but loaded into form.values.rpq by ClientRPQList.
 */
export const useOrderClientRpq = (form, enabled = true) => {
  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const clientId = form.values?.client?._id;
  const formRpq = form.values?.rpq;
  const clientRpq = form.values?.client?.rpq;
  const [fetchedRpq, setFetchedRpq] = useState(null);

  useEffect(() => {
    setFetchedRpq(null);
  }, [clientId]);

  useEffect(() => {
    if (!enabled || !clientId) return undefined;
    if (formRpq?.length || clientRpq?.length) return undefined;
    if (fetchedRpq !== null) return undefined;

    let cancelled = false;
    (async () => {
      const result = await apiRef.current.Client.getRiskRPQ(clientId);
      const rpq = result?.data?.rpq ?? [];
      if (cancelled) return;
      setFetchedRpq(rpq);
      if (rpq.length && form.setFieldValue) {
        form.setFieldValue("rpq", rpq);
        form.setFieldValue("rpqStatus", resolveRpqStatus(rpq));
      }
    })();

    return () => {
      cancelled = true;
    };
    // api via apiRef; form.setFieldValue is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, clientId, clientRpq, formRpq, fetchedRpq]);

  const rpq = formRpq?.length
    ? formRpq
    : clientRpq?.length
      ? clientRpq
      : fetchedRpq ?? [];

  return useMemo(
    () =>
      getClientRpqSummary({
        rpq,
        rpqStatus: form.values?.rpqStatus ?? form.values?.client?.rpqStatus,
        client: form.values?.client,
      }),
    [form.values?.client, form.values?.rpqStatus, rpq]
  );
};
